<template>
  <b-form @submit.prevent="submitForm(item)" novalidate>

    <b-row align-h="center" class="mb-4">
      <b-button v-if="item.projectState === null || item.projectState === this.PROJECT_STATE_DRAFT"
        :class="'mr-2' + buttonClass" type="submit" variant="primary"
        alt="Create/Save Changes to Your New Citizen Science Project Now"
        title="Create/Save Changes to Your New Citizen Science Project Now" @click="isDraft = true"
        :disabled="isLoading">
        {{ draftButtonTitle }}
      </b-button>
      &nbsp;&nbsp;
      <b-button
        v-if="item.projectState === null || item.projectState === this.PROJECT_STATE_DRAFT || item.projectState === this.PROJECT_STATE_PUBLISHED"
        :class="'ml-2' + buttonClass" type="submit" variant="primary"
        alt="Publish/Save Changes to Your New Citizen Science Project Now"
        title="Publish/Save Changes to Your New Citizen Science Project Now" @click="isDraft = false"
        :disabled="isLoading">
        {{ publishButtonTitle }}
      </b-button>
    </b-row>

    <VueLoader v-if="isLoading || isLoadingUpload || isLoadingUpdate || isOrganizationsLoading" />

    <b-card class="mb-2">
      <b-container ref="cont" style="padding: 16px !important;">

        <b-row>
          <b-col lg="7" md="12" class="mt-0">
            <b-form-group>
              <div class="img-wrapper" style="border: solid 0px red;">

                <!-- Show a toast notification about Logo and Banner images, autohide in 5 seconds -->
                <b-toast id="logo-save-toast" variant="danger" style="position: absolute; z-index: 1;"
                  title="Project logo upload incomplete." static no-close-button auto-hide-delay="5000">
                  Click 'Save' to Finish Uploading the Project Logo.
                </b-toast>
                <b-toast id="banner-save-toast" variant="danger" style="position: absolute; z-index: 1;"
                  title="Banner image upload incomplete." static no-close-button auto-hide-delay="5000">
                  Click 'Save' to Finish Uploading the Project Banner Image.
                </b-toast>
                <b-toast id="image-banner-required-toast" variant="danger" style="position: absolute; z-index: 1;"
                  title="An image is required." static no-close-button auto-hide-delay="5000">
                  Please upload a Banner Image for your Project.
                </b-toast>

                <div class="logo-overlay">
                  <div class="logo-wrapper">
                    <b-form-group>
                      <b-img class="logo-responsive" alt="Project Logo" title="Project Logo" v-model="currentLogoImage"
                        :src="currentLogoImage ? currentLogoImage : '/assets/images/generic-logo.png'" />
                      <div class="logo-img-overlay">
                        <b-btn class="btn btn-md btn-tertiary mt-4" variant="tertiary" v-if="!uploadInProgressLogo"
                          @click="uploadBannerClickToOpenPopUp($event, 'project_image', 'browse-file-dialog-logo', 'fileInputProjectLogoLabel')">
                          &nbsp;Upload Logo
                        </b-btn>
                        <b-btn variant="primary" class="btn btn-md btn-primary mt-4"
                          @click="onUploadProjectLogo('logoCropper')" v-if="uploadInProgressLogo"
                          :disabled="uploadIsLoading">
                          <font-awesome-icon icon="spinner" pulse v-show="uploadIsLoading" />
                          Save
                        </b-btn>
                        <b-btn class="btn btn-md btn-secondary mt-4" @click="onCancelProjectLogoImage"
                          v-if="uploadInProgressLogo">
                          Cancel
                        </b-btn>
                        <b-btn class="btn btn-md btn-danger mt-4" @click="onRemoveProjectLogoImage" variant="danger"
                          v-if="!uploadInProgressLogo && currentLogoImage" :disabled="uploadIsLoading">
                          <font-awesome-icon icon="spinner" pulse v-show="uploadIsLoading" />
                          Remove Logo
                        </b-btn>
                      </div>
                    </b-form-group>
                  </div>
                </div>

                <b-form-group> <!-- BANNER IMAGE AND BUTTONS -->
                  <b-container v-if="currentBannerImage !== null && currentBannerImage.length > 0" fluid
                    :style="{ 'background-image': 'url(' + currentBannerImage + ')' }"
                    class="HeaderContainer"></b-container>
                  <b-container v-else="currentBannerImage" fluid
                    :style="{ 'background-image': 'url(' + '/assets/images/generic-banner-image.png' + ')' }"
                    class="HeaderContainer"></b-container>

                  <div class="img-overlay">
                    <div style="display: block; color:grey; font-size:16px; margin-bottom:24px; margin-top:-24px;">
                      Project
                      Banner Image<br />(3MB or smaller)
                    </div>

                    <b-btn variant="tertiary"
                      @click="uploadBannerClickToOpenPopUp($event, '', 'browse-file-dialog', 'fileInputProjectBannerLabel')"
                      v-if="!uploadInProgressBanner">
                      Upload Banner
                    </b-btn>
                    <b-btn variant="primary" class="btn btn-md btn-primary" @click="onUploadProjectBanner('cropper')"
                      v-if="uploadInProgressBanner" :disabled="uploadIsLoading">
                      <font-awesome-icon icon="spinner" pulse v-show="uploadIsLoading" />
                      Save
                    </b-btn>
                    <b-btn class="btn btn-md btn-secondary" @click="onCancelProjectBannerImage"
                      v-if="uploadInProgressBanner">Cancel
                    </b-btn>
                    <b-btn class="btn btn-md btn-danger" @click="onRemoveProjectBannerImage"
                      v-if="!uploadInProgressBanner && currentBannerImage" :disabled="uploadIsLoading">
                      <font-awesome-icon icon="spinner" pulse v-show="uploadIsLoading" />
                      Remove Banner
                    </b-btn>
                  </div>
                </b-form-group>
              </div>
              <span style="color: red;">All fields are required</span>
              <b-form-group id="project-name-input-group" class="mt-4" label="Project Name"
                label-for="project-name-input" label-class="font-weight-bold" label-cols-sm="4" label-cols-lg="3">
                <b-form-input id="project-name-input" type="text" name="name" placeholder="Project Name"
                  v-model.trim="item.name" v-validate="'required|min:5|max:100'"
                  :class="{ 'is-invalid': errors.has('name') || errors.has('urlField') }" :disabled="isLoading"
                  :state="updateURLField" aria-describedby="project-name-input-live-feedback">
                </b-form-input>
                <b-form-invalid-feedback id="project-name-input-live-feedback">
                  {{ errors.first('name') || errors.first('urlField') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-url-input-group" class="mt-4" label="Project URL" label-for="project-url-input"
                label-class="font-weight-bold" label-cols-sm="4" label-cols-lg="3">
                <b-input-group-prepend>
                  <font-awesome-icon icon="question-circle" id="projectUrl-info" class="project-url-icon" />
                </b-input-group-prepend>
                <b-form-textarea id="project-url-textarea" name="urlField" placeholder="Project URL"
                  v-model="item.urlField" :disabled="true" :max-rows="2"
                  aria-describedby="project-url-input-live-feedback">
                </b-form-textarea>
              </b-form-group>

              <b-form-group id="project-description-input-group" label="About" label-for="project-description-input"
                label-class="font-weight-bold" label-cols-sm="4" label-cols-lg="3">
                <b-form-textarea id="project-description-textarea" name="description"
                  placeholder="Please provide a description of your project (Minimum of 10 characters)"
                  v-model="item && item.description" v-validate="'required|min:10|max:4000'"
                  :class="{ 'is-invalid': errors.has('description') }" :disabled="isLoading" :rows="3" :max-rows="6"
                  aria-describedby="project-description-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-description-input-live-feedback">
                  {{ errors.first('description') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-goals-input-group" label="Goals" label-for="project-goals-input"
                label-class="font-weight-bold" label-cols-sm="4" label-cols-lg="3">
                <b-form-textarea id="project-goals-textarea" name="goals"
                  placeholder="Please describe what your project hopes to accomplish (e.g. Monitor water quality in the St. Lucie River)"
                  v-model="item && item.goals" v-validate="'required|min:10|max:4000'"
                  :class="{ 'is-invalid': errors.has('goals') }" :disabled="isLoading" :rows="3" :max-rows="6"
                  aria-describedby="project-goals-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-goals-input-live-feedback">
                  {{ errors.first('goals') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group id="project-tasks-input-group" label="Tasks" label-for="project-tasks-input"
                label-class="font-weight-bold" label-cols-sm="4" label-cols-lg="3">
                <b-form-textarea id="project-tasks-textarea" name="tasks"
                  placeholder="Please describe how the participant will help this project (e.g. Collect and submit water samples for analysis)"
                  v-model="item && item.tasks" v-validate="'required|min:10|max:1000'"
                  :class="{ 'is-invalid': errors.has('tasks') }" :disabled="isLoading" :rows="3" :max-rows="6"
                  aria-describedby="project-tasks-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-tasks-input-live-feedback">
                  {{ errors.first('tasks') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="tabNameValue === 'EditProject'" id="project-gettingStarted-input-group" class="mt-4"
                label="Getting Started" label-for="project-gettingStarted-input" label-class="font-weight-bold"
                label-cols-sm="4" label-cols-lg="3">
                <b-form-textarea id="project-gettingStarted-input" name="gettingStarted"
                  placeholder="Please provide a description about how to get started with this project"
                  v-model="item && item.gettingStarted" :class="{ 'is-invalid': errors.has('gettingStarted') }"
                  :disabled="isLoading" :rows="3" :max-rows="6"
                  aria-describedby="project-gettingStarted-input-live-feedback">
                </b-form-textarea>
                <b-form-invalid-feedback id="project-gettingStarted-input-live-feedback">
                  {{ errors.first('gettingStarted') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="tabNameValue === 'EditProject'" id="project-website-input-group" class="mt-4"
                label="Website URL" label-for="project-website-input" label-class="font-weight-bold" label-cols-sm="4"
                label-cols-lg="3">
                <b-input-group-prepend>
                  <font-awesome-icon icon="question-circle" id="website-info" class="website-url-icon" />
                </b-input-group-prepend>
                <b-form-input id="project-website-input" type="text" maxlength="100" name="website"
                  placeholder="Website URL (e.g. https://citsci.org)" v-model.trim="item.website"
                  :class="{ 'is-invalid': errors.has('website') }" :disabled="isLoading"
                  aria-describedby="project-website-input-live-feedback">
                </b-form-input>
                <b-form-invalid-feedback id="project-website-input-live-feedback">
                  {{ errors.first('website') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="tabNameValue === 'EditProject'" id="project-facebookLink-input-group" class="mt-4"
                label="Facebook Page" label-for="facebook-name-input" label-class="font-weight-bold" label-cols-sm="4"
                label-cols-lg="3">
                <b-form-input id="project-facebookLink-input" type="text" maxlength="100" name="facebookLink"
                  placeholder="Facebook URL (e.g. https://www.facebook.com/CitSci.org/)"
                  v-model.trim="item.facebookLink" :class="{ 'is-invalid': errors.has('facebookLink') }"
                  :disabled="isLoading" aria-describedby="project-facebookLink-input-live-feedback">
                </b-form-input>
                <b-form-invalid-feedback id="project-facebookLink-input-live-feedback">
                  {{ errors.first('facebookLink') }}
                </b-form-invalid-feedback>
              </b-form-group>

              <b-form-group v-if="tabNameValue === 'EditProject'" id="project-twtLink-input-group" class="mt-4"
                label="Twitter URL" label-for="project-twtLink-input" label-class="font-weight-bold" label-cols-sm="4"
                label-cols-lg="3">
                <b-form-input id="project-twtLink-input" type="text" maxlength="100" name="twtLink"
                  placeholder="Twitter URL (e.g. https://twitter.com/citsci)" v-model.trim="item.twitterLink"
                  :class="{ 'is-invalid': errors.has('twtLink') }" :disabled="isLoading"
                  aria-describedby="project-twtLink-input-live-feedback">
                </b-form-input>
                <b-form-invalid-feedback id="project-twtLink-input-live-feedback">
                  {{ errors.first('twtLink') }}
                </b-form-invalid-feedback>
              </b-form-group>

            </b-form-group>
          </b-col>

          <b-col lg="5" md="12">
            <div class="card">
              <div class="card-header">
                <h4>
                  Settings
                  <i class="fa fa-xs fa-wrench"></i>
                </h4>
              </div>
              <div class="container p-0 ml-3 mb-2">
                <b-row>
                  <b-col sm="9" class="mt-2">
                    <b>Membership: {{ item.approveContacts ? 'Member-Based' : 'Open' }}</b>
                  </b-col>
                  <b-col sm="3" class="mt-2">
                    <b-btn size="sm" variant="primary" @click="showMembershipModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </b-btn>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="9" class="mt-2">
                    <b>Privacy: {{ item.isPrivate ? 'Private' : 'Public' }}</b>
                  </b-col>
                  <b-col sm="3" class="mt-2">
                    <b-btn size="sm" variant="primary" @click="showPrivacyModal()">
                      <i class="mr-2 fa fa-pencil"></i>Edit
                    </b-btn>
                  </b-col>
                </b-row>
              </div>
            </div>

            <Integrations :project-state="item.projectState ? item.projectState : 0" :is-airtable="item.isAirtable"
              :is-sci-starter="item.isSciStarter" :is-zooniverse="item.isZooniverse" class="mt-3">
            </Integrations>

            <b-card class="special mt-3">
              <b-card-body>
                <b-container class="pt-2">
                  <h4>Hub Affiliations&nbsp;&nbsp;<span class="h4-icon"><font-awesome-icon icon="sitemap" />&nbsp;
                      <font-awesome-icon icon="question-circle" id="organization-info" /></span></h4>
                  <b-row>
                    <b-col md="12" sm="12" class="mt-2">
                      <ProjectOrgAffiliations v-if="item.projectState === this.PROJECT_STATE_PUBLISHED"
                        :new-affiliations="newAffiliations"></ProjectOrgAffiliations>
                      <label v-else>Hub affiliations are available after you publish this project.</label>
                    </b-col>
                  </b-row>
                </b-container>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </b-card>

    <b-card class="mb-4 mt-4">
      <h4 slot="header" class="mb-0">Location
        <span style="font-size: 14px;">
          <font-awesome-icon icon="question-circle" id="project-location-info" />
        </span>
      </h4>
      <b-container class="pt-4">
        <span style="color: red;">Latitude and Longtiude are required</span>
        <vue-google-map :item="item" :latitude="item && item.latitude" :longitude="item && item.longitude"
          :is-loading="isLoading" :custom-marker="false" :zoom-map-to-markers="11" :show-locate-address="true"
          :zoom-val="5" :show-address-flag="true" class="project-create-edit" />
      </b-container>
    </b-card>

    <b-row align-h="center">
      <b-button v-if="item.projectState === null || item.projectState === this.PROJECT_STATE_DRAFT"
        :class="'mr-2' + buttonClass" type="submit" variant="primary"
        alt="Create/Save Changes to Your New Citizen Science Project Now"
        title="Create/Save Changes to Your New Citizen Science Project Now" @click="isDraft = true"
        :disabled="isLoading">
        {{ draftButtonTitle }}
      </b-button>
      &nbsp;&nbsp;
      <b-button
        v-if="item.projectState === null || item.projectState === this.PROJECT_STATE_DRAFT || item.projectState === this.PROJECT_STATE_PUBLISHED"
        :class="'ml-2' + buttonClass" type="submit" variant="primary"
        alt="Publish/Save Changes to Your New Citizen Science Project Now"
        title="Publish/Save Changes to Your New Citizen Science Project Now" @click="isDraft = false"
        :disabled="isLoading">
        {{ publishButtonTitle }}
      </b-button>
    </b-row>

    <!--  <b-tooltip target="published-info" placement="bottom">-->
    <!--    Choose whether project is published (displayed to users) or draft (displayed only to project members)-->
    <!--  </b-tooltip>-->

    <b-tooltip target="membership-info" placement="bottom">
      Choose whether project membership is open (anyone can join without your approval) or closed (requests to join
      require your approval)
    </b-tooltip>

    <b-tooltip target="projectUrl-info" placement="right">
      This will be the web address you can use to have volunteers access your citizen science project directly. Simply
      have your volunteers copy and paste this into their web browser to easily access your project
    </b-tooltip>

    <b-tooltip target="website-info" placement="right">
      External website URL
    </b-tooltip>

    <b-tooltip target="privacy-info" placement="bottom">
      Choose whether your project data are public (visible to all) or private (only visible to members)
    </b-tooltip>

    <b-tooltip target="scistarter-info" placement="bottom">
      Your project info will be sent to SciStarter which can help to improve discovery and participation. Please note
      that
      your project will go through a manual approval step before it can be public visible and searchable.
    </b-tooltip>

    <b-tooltip target="organization-info" placement="bottom">
      Associate your project with one or more Organizations and become part of their Hub Page
    </b-tooltip>

    <b-tooltip target="zooniverse-info" placement="bottom">
      Connect project to Zooniverse for image processing
    </b-tooltip>

    <b-tooltip target="airtable-info" placement="bottom">
      Connect project to Airtable for data transfer
    </b-tooltip>

    <b-tooltip target="project-location-info" placement="bottom">
      Manually enter coordinates, click on the map, or search for an address to obtain coordinates. This represents your
      project headquarters. Please use decimal degrees (not degrees, minutes, seconds).
    </b-tooltip>
    <VueLoader v-if="isLoading || isLoadingUpload || isLoadingUpdate || isOrganizationsLoading" />

    <b-modal ref="browseFileDialogBanner" id="browse-file-dialog" title="Browse File Dialog" centered
      no-close-on-backdrop hide-footer>
      <b-alert variant="danger" role="alert" :show="!!error" dismissible @dismissed="this.resetError()">
        <font-awesome-icon icon="exclamation-triangle" />
        {{ error }}
      </b-alert>
      <div>
        <div class="modal-body">
          <b-row v-if="isPictureLoaded">
            <b-col>
              <b-container class="img-container">
                <img ref="imageTest" alt="Project Banner" title="Project Banner" :src="currentBannerImageToCrop" />
              </b-container>
            </b-col>
          </b-row>
          <div class="input-group mb-3">
            <div class="input-group-prepend">
              <span class="input-group-text">Upload</span>
            </div>
            <div class="custom-file">
              <input type="file" class="custom-file-input" ref="fileInputProjectBannerLabel" id="inputGroupFile01"
                accept="image/*" @change="onFilePickedProjectBanner">
              <label class="custom-file-label mt5" ref="fileInputProjectBannerLabel" for="inputGroupFile01">Choose
                file</label>
            </div>
          </div>
        </div>
        <div class="float-right" v-if="isPictureLoaded">
          <button class="btn btn-primary" @click="onCropImage('browseFileDialogBanner', 'cropper')">Crop Image</button>
          <button class="btn btn-secondary" @click="onNoClick('browseFileDialogBanner', 'fileInputProjectBannerLabel')">
            Cancel
          </button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="browseFileDialogLogo" id="browse-file-dialog-logo" title="Browse File Dialog" centered
      no-close-on-backdrop hide-footer>
      <b-alert variant="danger" role="alert" :show="!!error" dismissible @dismissed="this.resetError()">
        <font-awesome-icon icon="exclamation-triangle" />
        {{ error }}
      </b-alert>
      <div>
        <div class="modal-body">
          <b-row v-if="isLogoLoaded">
            <b-col>
              <b-container class="img-container" style="max-height: 360px;max-width: 440px;">
                <img ref="imageTestLogo" alt="Project Logo" title="Project Logo" :src="currentLogoImageToCrop" />
              </b-container>
            </b-col>
          </b-row>
          <div class="input-group mb-3">
            <div class="input-group-prepend">
              <span class="input-group-text">Upload</span>
            </div>
            <div class="custom-file">
              <input type="file" class="custom-file-input" ref="fileInputProjectLogo" id="inputGroupFile02"
                accept="image/*" @change="onFilePickedProjectLogo">
              <label class="custom-file-label mt5" ref="fileInputProjectLogoLabel" for="inputGroupFile02">Choose
                file</label>
            </div>
          </div>
        </div>
        <div class="float-right" v-if="isLogoLoaded">
          <button class="btn btn-primary" @click="onCropImage('browseFileDialogLogo', 'logoCropper')">Crop Image
          </button>
          <button class="btn btn-secondary" @click="onNoClick('browseFileDialogLogo', 'fileInputProjectLogo')">Cancel
          </button>
        </div>
      </div>
    </b-modal>

    <ProjectMembershipModal :membershipValue="item.approveContacts" @membershipChange="handleMembershipChange">
    </ProjectMembershipModal>
    <ProjectPrivacyModal :privacyValue="item.isPrivate" @privacyChange="handlePrivacyChange"></ProjectPrivacyModal>

  </b-form>

</template>
<script>
import VueGoogleMap from "../../assets/scripts/layout/GoogleMap";
import VueLoader from "../layout/Loader.vue";
import { mapGetters, mapActions } from 'vuex'
import Cropper from 'cropperjs';
import { appRootUrl } from "../../main";
import Integrations from "../../components/project/Integrations.vue";
import ProjectOrgAffiliations from "../../components/project/view/edit/ProjectOrgAffiliations.vue";
import ProjectMembershipModal from "../../components/project/view/edit/ProjectMembershipModal.vue";
import ProjectPrivacyModal from "../../components/project/view/edit/ProjectPrivacyModal.vue";

export default {
  inject: ['parentValidator'],

  components: {
    ProjectPrivacyModal, ProjectMembershipModal,
    VueGoogleMap,
    VueLoader,
    Integrations,
    ProjectOrgAffiliations,
  },

  data() {
    return {
      isPictureLoaded: false,
      isLogoLoaded: false,
      cropper: {},
      logoCropper: {},
      destination: null,
      imageTest: {},
      imageTestLogo: {},
      isImageLogo: false, /*Just to differentiate between banner and logo image; we have used this flag*/
      uploadFieldReady: true,
      uploadInProgressLogo: false,
      uploadInProgressBanner: false,
      uploadIsLoading: false,
      fdProfile: new FormData(),
      fdBanner: new FormData(),
      project: null,
      itemVal: this.values,
      itemSave: null,
      currentLogoImage: null,
      currentBannerImage: null,
      oldLogoImage: '',
      oldBannerImage: '',
      organizations: [],
      isDraft: false,
      tabNameValue: this.tabName,
      currentLogoImageToCrop: '',
      currentBannerImageToCrop: '',
      orgRequests: [],
    }
  },

  props: {
    tabName: {
      type: String,
      default: () => {
        return ''
      }
    },
    handleSubmit: {
      type: Function,
      required: true
    },
    handleUpdateField: {
      type: Function,
      required: true
    },
    values: {
      type: Object,
      required: true
    },
    projectErrors: {
      type: Object,
      default: () => {
      }
    },
    initialValues: {
      type: Object,
      default: () => {
      }
    },
    publishButtonTitle: {
      type: String,
      default: () => {
        return 'Publish'
      }
    },
    draftButtonTitle: {
      type: String,
      default: () => {
        return 'Draft'
      }
    },
    isLoading: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    buttonClass: {
      type: String,
      default: () => {
        return ''
      }
    }
  },

  computed: {
    ...mapGetters({
      isLoadingUpload: 'project/show/isLoading',
      isLoadingUpdate: 'project/update/isLoading',
      error: 'project/show/error',

      organizationsError: 'organization/list/organizationsError',
      isOrganizationsLoading: 'organization/list/isOrganizationsLoading',
      organizationsList: 'organization/list/organizations',
    }),

    // eslint-disable-next-line
    item: {
      get() {
        return this.itemVal
      },
      set(data) {
        this.itemVal = data
      }
    },

    selectedOrganizations: {
      get() {
        if (this.item) {
          return [...this.itemVal.organizationProjects.map((op) => op.organization)]
        } else {
          return null
        }
      },
      set(data) {
        // console.log(data)
        this.itemVal.organizationProjects = data
      }
    },

    /**
     * @function updateUrlField
     * @returns {boolean}
     */
    updateURLField() {
      //Strip all special characters except and replace with hyphen
      const special_char_regex = /(\s+|\/|\\|\[|\]|\{|\}|\,|\<|\>|\:|\;|\"|\.|\?|\-|\=|\+|\*|\&|\^|\%|\#|\@|\`|\'|\||\~|\!|\$|\*|\(|\)|\_|\$)+/g;
      const non_ascii_regex = /[\x00-\x08\x0E-\x1F\x7F-\uFFFF]+/g;
      const replace_char = '-'
      this.item.urlField = '';

      // If the name is filled, the url is created
      // - basically we replace all special characters and non-ascii values with a hyphen
      if (this.item.name && this.item.name !== '') {
        // Non-ascii characters (e.g. emoji etc...) are replaced followed by all special character
        let sanitisedUrl = this.item.name.toLowerCase()
        sanitisedUrl = sanitisedUrl.replace(special_char_regex, replace_char).replace(non_ascii_regex, replace_char)
        // If the URL ends with a hyphen, it is also removed
        if (sanitisedUrl.endsWith('-')) {
          sanitisedUrl = sanitisedUrl.slice(0, -1)
        }
        // Sanitised URL is set <safe to have only hyphen in the url>
        this.item.urlField = appRootUrl + '/projects/' + sanitisedUrl
      }
      return true;
    },

    violations() {
      return this.projectErrors || {}
    },

    // // Computed property for Project sciStarterStatus => HasSciStarterProject|DoesNotHaveProject
    // // Translate integer into boolean for toggle's v-model
    // sciStarterStatus: {
    //    get() {
    //      if (this.item.sciStarterID) { // if it has a value (is not null)
    //        return true
    //      } else {
    //        return false
    //      }
    //    },
    //    set(data) {
    //      if (data) {
    //        this.item.sciStarterID = 0 // set to 0 upon toggle initially
    //      } else {
    //        this.item.sciStarterID = null
    //      }
    //    },
    // }

  },

  methods: {
    ...mapActions({
      newUpload: 'project/show/newUpload',
      uploadInProgress: 'project/show/uploadInProgress',
      uploadFile: 'project/show/uploadFile',
      resetError: 'project/show/resetError',
      setErrorShow: 'project/show/setError',
      setErrorCreate: 'project/create/setError',
      getOrganizations: 'organization/list/getOrganizations',
      createSciStarterProject: 'project/show/createSciStarterProject'
    }),

    uploadBannerClickToOpenPopUp(event, type, modalName, ref) {
      event.stopPropagation();
      event.preventDefault();
      if (type !== '') {
        this.isImageLogo = true;
        this.$root.$emit('bv::show::modal', modalName);
      } else {
        this.isImageLogo = false;
        this.$root.$emit('bv::show::modal', modalName);
      }
      setTimeout(() => {
        this.onUploadClick(this.$refs[ref]);
      });
    },

    onNoClick(bModalRef, fileInputRef) {
      this.currentBannerImageToCrop = '';
      this.currentLogoImageToCrop = '';
      this.isPictureLoaded = false;
      this.isLogoLoaded = false;
      this.isImageLogo = false;
      this.$refs[fileInputRef].value = null;
      this.$refs[bModalRef].hide();
    },

    showMembershipModal() {
      this.$bvModal.show('project-membership-modal')
    },

    handleMembershipChange(newVal) {
      this.item.approveContacts = newVal
    },

    showPrivacyModal() {
      this.$bvModal.show('project-privacy-modal')
    },

    handlePrivacyChange(newVal) {
      this.item.isPrivate = newVal
    },

    /**
     * @function isInvalid
     * @param key
     * @returns {boolean|*}
     */
    isInvalid(key) {
      return Object.keys(this.violations).length > 0 && this.violations[key]
    },

    /**
     * @function onFilePickedProjectBanner
     * @param files
     * @returns {void|*}
     */
    onFilePickedProjectBanner(files) {

      if (this.cropper && this.cropper.canvas) {
        this.isPictureLoaded = false;
      }
      this.destroyCropper('project_banner');

      const _files = files.target.files || files.dataTransfer.files
      const fileTypesAllowed = ['jpg', 'jpeg', 'png']

      if (!_files.length)
        return

      const filename = _files[0].name

      if (filename.lastIndexOf('.') <= 0 || fileTypesAllowed.indexOf(filename.split('.').pop().toLowerCase()) == -1) {
        return this.setErrorShow('Please select a valid file')
      }

      if (_files[0].size > 100000 * 10 * 10) {
        return this.setErrorShow('Maximum filesize is 10M')
      }

      this.newUpload().then(() => {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          this.currentBannerImageToCrop = e.target.result;
          this.isPictureLoaded = true;
          setTimeout(() => {
            this.imageTest = this.$refs.imageTest;
            this.setCropperData(this.imageTest, 36 / 9, 440, 110, 'cropper')
          }, 250);
        };
        fileReader.readAsDataURL(_files[0]);
      })
    },

    /**
     * @function onFilePickedProjectLogo
     * @param files
     * @returns {void|*}
     */
    onFilePickedProjectLogo(files) {

      if (this.logoCropper && this.logoCropper.canvas) {
        this.isLogoLoaded = false;
      }
      this.destroyCropper('project_image');

      const _files = files.target.files || files.dataTransfer.files
      const fileTypesAllowed = ['jpg', 'jpeg', 'png']

      if (!_files.length)
        return

      const filename = _files[0].name

      if (filename.lastIndexOf('.') <= 0 || fileTypesAllowed.indexOf(filename.split('.').pop().toLowerCase()) == -1) {
        return this.setErrorShow('Please select a valid file')
      }

      if (_files[0].size > 100000 * 10 * 10) {
        return this.setErrorShow('Maximum filesize is 10M')
      }

      this.newUpload().then(() => {
        const fileReader = new FileReader();

        fileReader.onload = (e) => {
          this.currentLogoImageToCrop = e.target.result;
          this.isLogoLoaded = true;
          setTimeout(() => {
            this.imageTestLogo = this.$refs.imageTestLogo;
            this.setCropperData(this.imageTestLogo, 1, 100, 100, 'logoCropper')
          }, 250);
        };
        fileReader.readAsDataURL(_files[0]);
      })
    },

    setCropperData(imageRef, aspectRation, width, height, cropperName) {
      this[cropperName] = new Cropper(imageRef, {
        zoomable: false,
        scalable: false,
        highlight: false,
        aspectRatio: aspectRation,
        ready: (e) => {
          var imageData = this[cropperName].getImageData();
          this[cropperName].options.minCropBoxWidth = imageData.width;
          this[cropperName].options.minCropBoxHeight = imageData.height;
          //this.cropper.limitCropBox(true, false);

          this[cropperName].setCropBoxData({
            width: width,
            height: height
          })
        },
        dragCrop: false
      });
    },

    /**
     * @function onCropImage
     * @returns {void|*}
     */
    onCropImage(ref, cropperName) {
      this.destination = null;
      const canvas = this[cropperName].getCroppedCanvas();
      this.destination = canvas.toDataURL("image/png");
      if (this.isImageLogo) {
        this.currentLogoImage = this.destination;
        this.uploadInProgressLogo = true;
      } else {
        this.currentBannerImage = this.destination;
        this.uploadInProgressBanner = true;
      }
      this.$refs[ref].hide();

    },
    /**
     * @function onUploadProjectLogo
     */
    onUploadProjectLogo(cropperName) {
      const canvas = this[cropperName].getCroppedCanvas();

      canvas.toBlob((blob) => {
        this.fdProfile.append('file', blob, 'logo.png');
        this.fdProfile.append('actionType', 'project_image');

        this.uploadFile({ fd: this.fdProfile, isCreateRes: true, currentId: '' }).then((data) => {
          this.oldLogoImage = this.values.picture;
          this.$emit('item-changed', { picture: data });
          this.uploadInProgressLogo = false;
        })
      });
    },

    /**
     * @function onUploadProjectBanner
     */
    onUploadProjectBanner(cropperName) {
      const canvas = this[cropperName].getCroppedCanvas();

      canvas.toBlob((blob) => {
        this.fdBanner.append('file', blob, 'banner.png');
        this.fdBanner.append('actionType', 'project_banner');

        this.uploadFile({ fd: this.fdBanner, isCreateRes: true, currentId: '' }).then((data) => {

          if (typeof data == 'undefined' || data == null || data == false) {
            this.setErrorCreate('Upload maximum size exceeded!');
            return
          }

          this.oldBannerImage = this.values.bannerPicture;
          this.$emit('item-changed', { bannerPicture: data });

          this.uploadInProgressBanner = false
        })
      })
    },

    /**
     * @function onCancelProjectLogoImage
     */
    onCancelProjectLogoImage() {
      this.uploadInProgressLogo = false
      this.currentLogoImage = null
      let oldPicture = null

      if (this.oldLogoImage && this.oldLogoImage.picture && this.oldLogoImage.picture.path) {
        this.currentLogoImage = this.oldLogoImage.picture.path
        oldPicture = this.oldLogoImage.picture['@id']
      }

      //this.$refs.fileInputProjectLogo.value = null
      this.isLogoLoaded = false;
      this.$emit('item-changed', { picture: this.oldLogoImage })
    },

    /**
     * @function onCancelProjectBannerImage
     */
    onCancelProjectBannerImage() {
      this.uploadInProgressBanner = false
      this.currentBannerImage = null
      let oldPicture = null

      if (this.item && this.item.bannerPicture && this.item.bannerPicture.path) {
        this.currentBannerImage = this.item.bannerPicture.path
      }

      if (this.oldBannerImage && this.oldBannerImage.picture && this.oldBannerImage.picture.path) {
        this.currentBannerImage = this.oldBannerImage.picture.path
        oldPicture = this.oldLogoImage.picture['@id']
      }

      //this.$refs.fileInputProjectBanner.value = null
      this.isPictureLoaded = false;
      this.$emit('item-changed', { bannerPicture: oldPicture })
    },

    /**
     * @function onRemoveProjectBannerImage
     */
    onRemoveProjectBannerImage() {
      this.currentBannerImage = null;
      this.uploadInProgressBanner = false;
      //this.$refs.fileInputProjectBanner.value = null
      this.$emit('item-changed', { bannerPicture: null })
    },

    /**
     * @function removeLogo
     */
    onRemoveProjectLogoImage() {
      this.currentLogoImage = null;
      this.uploadInProgressLogo = false;
      //this.$refs.fileInputProjectLogo.value = null
      this.$emit('item-changed', { picture: null })

    },

    destroyCropper(cropperToDestroy) {
      if (cropperToDestroy === 'project_banner' && this.cropper && this.cropper.canvas) {
        this.cropper.destroy();
      }
      if (cropperToDestroy === 'project_image' && this.logoCropper && this.logoCropper.canvas) {
        this.logoCropper.destroy();
      }
    },

    /**
     * @function onUploadClick
     */
    onUploadClick(refFileInput) {
      //this.destroyCropper();
      this.setErrorCreate();
      refFileInput.click();

    },

    newAffiliations(newOrgs) {
      this.orgRequests = newOrgs
    },

    /**
     * @function submitForm - submit the form to the API for saving
     * @param {Object} item
     */
    submitForm(item) {
      if (this.uploadInProgressLogo) {
        window.scrollTo(0, 0);
        this.$bvToast.show('logo-save-toast');
        return;
      }
      if (this.uploadInProgressBanner) {
        window.scrollTo(0, 0);
        this.$bvToast.show('banner-save-toast');
        return;
      }

      // // Allow projects to be created without an image
      // //  - when saving as draft
      // //  - when editing a draft/already published project
      // if ((item.hasOwnProperty('id') && !this.isDraft) || (!item.hasOwnProperty('id'))) {
      //   if ((this.currentBannerImage === null && this.currentLogoImage === null)) {
      //     window.scrollTo(0,0);
      //     this.$bvToast.show('image-banner-required-toast');
      //     return;
      //   }
      // }

      // Send the draft\publish button click
      // console.log(this.currentBannerImage, this.currentLogoImage)
      this.handleSubmit(item, this.orgRequests, this.isDraft);
    }
  },

  created() {

    this.$validator = this.parentValidator

    if (this.values) {
      this.currentLogoImage = this.values && this.values.picture && this.values.picture.path ? this.values.picture.path : null
      this.currentBannerImage = this.values && this.values.bannerPicture && this.values.bannerPicture.path ? this.values.bannerPicture.path : null
    }

    // Get the Organizations for the picklist
    // this is done asynchronously
    this.getOrganizations();
  }
}

</script>

<style lang="scss" scoped>
.card-body {
  padding: 0rem !important;
}

.vue-js-switch#scistarter-btn {
  font-size: 12px;
}

input::placeholder,
textarea::placeholder,
#project-longitude-input::placeholder {
  color: #0B7FC7;
  opacity: .54;
}

.HeaderContainer {
  background-size: cover;
  background-position: center bottom;
  background-color: grey;
  height: 200px;
  overflow: hidden;
  text-align: right;
}

img {
  max-width: 100% !important;
  /* This rule is very important, please do not ignore this! */
}

.img-wrapper {
  position: relative;
}

.cropper-wrap-box {
  bottom: 38px;
  top: -13px;
}

.logo-wrapper {
  position: relative;
  border: solid 1px green;
  top: 0px;
  height: 120px;
}

.img-responsive {
  width: 100%;
  height: 200px;
}

.logo-responsive {
  width: 100%;
  height: 120px;
}

.logo-overlay {
  position: absolute;
  width: 160px;
  height: 120px;
  top: 20px;
  bottom: 0;
  left: auto;
  right: 20px;
  text-align: center;
  /*background-color: #fff;*/
}

.logo-img-overlay {
  position: absolute;
  top: 0px;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 99;
}

.img-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
}

.img-overlay:before {
  content: ' ';
  display: block;
  /* adjust 'height' to position overlay content vertically */
  height: 45%;
}

.card-body {
  /*background-color: #F5F4E8 !important; */
}

.card-header {
  background-color: #F5F4E8 !important;
}

.disabled {
  cursor: not-allowed;
}

h1,
h2,
h3,
h4 {
  vertical-align: middle;
}

h1>.h4-icon,
h2>.h4-icon,
h3>.h4-icon,
h4>.h4-icon {
  vertical-align: middle;
  margin-top: -12px;
  /* -0.5em */
  font-size: 12px;
  border: solid 0px red;
}

.img-container>img {
  max-width: 100%;
  width: 440px;
  max-height: 360px;
  max-width: 440px;
}

.project-url-icon {

  @media screen and (max-width: 576px) {
    position: absolute;
    top: -25px;
    left: 85px;
  }

  @media (min-width: 577px) and (max-width: 667px) {
    position: absolute;
    top: 8px;
    left: -65px;
  }

  @media (min-width: 668px) and (max-width: 767px) {
    position: absolute;
    top: 8px;
    left: -67px;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    position: absolute;
    top: 8px;
    left: -120px;
  }

  @media (min-width: 992px) and (max-width: 1115px) {
    position: absolute;
    top: 8px;
    left: -28px;
  }

  position: absolute;
  top: 8px;
  left: -38px;

}

.website-url-icon {

  @media screen and (max-width: 576px) {
    position: absolute;
    top: -25px;
    left: 95px;
  }

  @media (min-width: 577px) and (max-width: 667px) {
    position: absolute;
    top: 8px;
    left: -55px;
  }

  @media (min-width: 668px) and (max-width: 767px) {
    position: absolute;
    top: 8px;
    left: -60px;
  }

  @media (min-width: 768px) and (max-width: 991px) {
    position: absolute;
    top: 8px;
    left: -110px;
  }

  @media (min-width: 992px) and (max-width: 1115px) {
    position: absolute;
    top: 8px;
    left: -18px;
  }

  position: absolute;
  top: 8px;
  left: -30px;

}
</style>
